import { randomUUID } from "@/lib/randomUUID";
import type { ChatHistoryState, ChatSession, ChatSessionsState } from "@/types/aiAgent";
import { createSafeLocalStorage } from "@/lib/storage";
import { getItem as idbGetItem, removeItem as idbRemoveItem, isIndexedDbAvailable } from "@/lib/indexedDbKeyValue";
import { chatHistoryService } from "@/services/chatHistoryService";
import { artifactService } from "@/services/artifactService";
import { convertReportXmlToMarkdown } from "@/lib/reportMigration";

export const CHAT_HISTORY_STORAGE_KEY = "wyni-chat-sessions";
export const CHAT_HISTORY_OLD_STORAGE_KEY = "wyni-chat-history";
export const CHAT_HISTORY_ACTIVE_SESSION_KEY = "wyni-chat-active-session";
export const CHAT_HISTORY_MIGRATION_FLAG_KEY = "wyni-chat-migration-complete";
export const CHAT_HISTORY_STORAGE_VERSION = 1;

type SafeLocalStorage = ReturnType<typeof createSafeLocalStorage>;

export const generateSessionTitle = (content: string): string => {
  const maxLength = 50;
  if (content.length <= maxLength) return content;

  const truncated = content.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");

  return lastSpace > 30
    ? `${truncated.substring(0, lastSpace)}...`
    : `${truncated}...`;
};

const migrateFromOldFormat = (safeLocalStorage: SafeLocalStorage): ChatSessionsState | null => {
  try {
    const oldData = safeLocalStorage.getItem(CHAT_HISTORY_OLD_STORAGE_KEY);

    if (!oldData) return null;

    const parsed: ChatHistoryState = JSON.parse(oldData);
    if (!parsed.messages || parsed.messages.length === 0) return null;

    const sessionId = randomUUID();
    const firstUserMessage = parsed.messages.find((m) => m.role === "user");

    const seenIds = new Set();
    const uniqueMessages = parsed.messages.filter((msg) => {
      if (seenIds.has(msg.id)) return false;
      seenIds.add(msg.id);
      return true;
    });

    const session: ChatSession = {
      id: sessionId,
      title: firstUserMessage
        ? generateSessionTitle(firstUserMessage.content)
        : "Migrated Chat",
      messages: uniqueMessages,
      createdAt: uniqueMessages[0]?.timestamp || new Date().toISOString(),
      lastUpdated: parsed.lastUpdated || new Date().toISOString(),
      messagesLoaded: true,
    };

    safeLocalStorage.removeItem(CHAT_HISTORY_OLD_STORAGE_KEY);

    return {
      sessions: { [sessionId]: session },
      activeSessionId: sessionId,
      version: CHAT_HISTORY_STORAGE_VERSION,
    };
  } catch (error) {
    console.error("Migration failed:", error);
    return null;
  }
};

export const loadLocalChatSessions = async (
  safeLocalStorage: SafeLocalStorage
): Promise<ChatSessionsState | null> => {
  let stored: string | null = null;
  if (isIndexedDbAvailable()) {
    stored = await idbGetItem(CHAT_HISTORY_STORAGE_KEY);
  }
  if (!stored) {
    stored = safeLocalStorage.getItem(CHAT_HISTORY_STORAGE_KEY);
  }

  if (stored) {
    const parsed: ChatSessionsState = JSON.parse(stored);
    if (!parsed.version) {
      return migrateFromOldFormat(safeLocalStorage);
    }
    if (!parsed.sessions || typeof parsed.sessions !== "object") {
      return null;
    }
    return parsed;
  }

  return migrateFromOldFormat(safeLocalStorage);
};

export const clearLocalChatHistory = async (safeLocalStorage: SafeLocalStorage) => {
  safeLocalStorage.removeItem(CHAT_HISTORY_STORAGE_KEY);
  safeLocalStorage.removeItem(CHAT_HISTORY_OLD_STORAGE_KEY);
  safeLocalStorage.removeItem(CHAT_HISTORY_ACTIVE_SESSION_KEY);
  if (isIndexedDbAvailable()) {
    await idbRemoveItem(CHAT_HISTORY_STORAGE_KEY);
  }
};

export const migrateLocalChatSessionsToBackend = async (
  localState: ChatSessionsState,
  safeLocalStorage: SafeLocalStorage
) => {
  const artifactMap = new Map<string, { artifactId: string; versionNumber: number; versionId?: string }>();
  const artifactInputs: Array<{
    id: string;
    project_id: string;
    title: string;
    type: string;
    versions: Array<{
      id?: string;
      version_number: number;
      content: string;
      change_summary?: string | null;
      citations?: Record<string, any>[] | null;
    }>;
  }> = [];

  const artifactById = new Map<string, {
    id: string;
    project_id: string;
    title: string;
    type: string;
    versions: Map<number, {
      id: string;
      version_number: number;
      content: string;
      change_summary?: string | null;
      citations?: Record<string, any>[] | null;
    }>;
  }>();

  const isUuid = (value?: string | null) => {
    if (!value) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  };

  Object.values(localState.sessions).forEach((session) => {
    session.messages.forEach((message: any) => {
      if (!message.reportXml) return;

      const chain = isUuid(message.reportVersionChain)
        ? message.reportVersionChain
        : randomUUID();

      const artifactId = chain;
      const versionNumber = message.reportVersionNumber || 1;
      const versionId = randomUUID();
      const { title, markdown, citations } = convertReportXmlToMarkdown(
        message.reportXml,
        message.reportCitations
      );

      let artifact = artifactById.get(artifactId);
      if (!artifact) {
        artifact = {
          id: artifactId,
          project_id: session.id,
          title: title || "Report",
          type: "text/markdown",
          versions: new Map(),
        };
        artifactById.set(artifactId, artifact);
      }

      if (!artifact.versions.has(versionNumber)) {
        artifact.versions.set(versionNumber, {
          id: versionId,
          version_number: versionNumber,
          content: markdown,
          change_summary: message.content || null,
          citations,
        });
      }

      artifactMap.set(message.id, { artifactId, versionNumber, versionId });
    });
  });

  artifactById.forEach((artifact) => {
    artifactInputs.push({
      id: artifact.id,
      project_id: artifact.project_id,
      title: artifact.title,
      type: artifact.type,
      versions: Array.from(artifact.versions.values()),
    });
  });

  const artifactVersionLookup = new Map<string, Map<number, string>>();
  const artifactImportStatus = new Map<string, { versionsSkipped: number }>();
  if (artifactInputs.length > 0) {
    const importResult = await artifactService.bulkImport({ artifacts: artifactInputs });
    if (importResult?.results) {
      importResult.results.forEach((result: any) => {
        if (result?.artifact_id) {
          artifactImportStatus.set(result.artifact_id, {
            versionsSkipped: result.versions_skipped ?? 0,
          });
        }
      });
    }

    const versionResponses = await Promise.all(
      Array.from(artifactById.keys()).map(async (artifactId) => {
        try {
          const response = await artifactService.listVersions(artifactId);
          return { artifactId, versions: response.versions || [] };
        } catch (error) {
          console.error("Failed to load artifact versions:", error);
          return { artifactId, versions: [] };
        }
      })
    );

    versionResponses.forEach(({ artifactId, versions }) => {
      const versionMap = new Map<number, string>();
      versions.forEach((version: any) => {
        if (version?.version_number && version?.id) {
          versionMap.set(Number(version.version_number), version.id);
        }
      });
      if (versionMap.size > 0) {
        artifactVersionLookup.set(artifactId, versionMap);
      }
    });
  }

  const sessionPayloads = Object.values(localState.sessions).map((session) => ({
    id: session.id,
    title: session.title,
    created_at: session.createdAt,
    updated_at: session.lastUpdated,
    messages: session.messages.map((message: any) => ({
      id: message.id,
      role: message.role,
      content: message.content,
      timestamp: message.timestamp,
      sources: message.sources ?? null,
      citations: message.citations ?? null,
      tool_calls: message.toolCalls ?? null,
      tool_invocations: message.toolInvocations ?? null,
      content_blocks: message.contentBlocks ?? null,
      recommendations: message.recommendations ?? null,
      rating: message.rating ?? null,
      metadata: message.metadata ?? null,
      artifact_id: artifactMap.get(message.id)?.artifactId ?? null,
      artifact_version_id: (() => {
        const mapped = artifactMap.get(message.id);
        if (!mapped) return null;
        const resolved = artifactVersionLookup.get(mapped.artifactId)?.get(mapped.versionNumber);
        if (resolved) return resolved;
        const importStatus = artifactImportStatus.get(mapped.artifactId);
        if (importStatus && importStatus.versionsSkipped === 0) {
          return mapped.versionId ?? null;
        }
        return null;
      })(),
    })),
  }));

  await chatHistoryService.bulkImport({ sessions: sessionPayloads });
  await clearLocalChatHistory(safeLocalStorage);
};
